import { 
    Component,
    ElementRef, 
    ViewChild,
    OnDestroy,
    AfterViewInit,
} from '@angular/core';

import { Subscription } from 'rxjs';

import {
    FormControl,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { TimeOff } from 'src/app/models/timeOff.model';
import { TimeOffService } from 'src/app/services/time-off.service';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { DialogHeaderComponent } from '../../dialog-header/dialog-header.component';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { TimepickerUI } from 'timepicker-ui';
import { ThemeService } from 'src/app/services/theme.service';
import { PickerTheme, TimePickerType, StartOrEndTime } from 'src/app/types/time-picker-type';
import { ITimeOffControls } from 'src/app/interfaces/time-off-control-interface';
import { AM, AMTIMESTART, STARTTIME, ENDTIME, PM, PMTIMEADJUSTMENT, PMTIMESTART, LIGHTTHEME, PORTALDARKTHEME, PORTALLIGHTTHEME, DARKTHEME } from 'src/app/constants/calendarConstants';

@Component({
    selector: 'app-create-time-off-dialog',
    templateUrl: './create-time-off-dialog.component.html',
    styleUrls: ['./create-time-off-dialog.component.scss'],
    imports: [
        DialogHeaderComponent,
        MatDividerModule,
        MatDialogContent,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatInputModule,
        MatTimepickerModule,
        MatOption,
        MatSelect,
        MatButton,
    ],
})
export class CreateTimeOffDialogComponent implements AfterViewInit, OnDestroy {
    
    timeOffForm: FormGroup<ITimeOffControls> = new FormGroup({
        startDate: new FormControl<Date | null>(null, [Validators.required]),
        endDate: new FormControl<Date | null>(null, [Validators.required]),
        startTimeDisplay: new FormControl<string>('', [Validators.required]),
        endTimeDisplay: new FormControl<string>('', [Validators.required]),
        timeOffDescription: new FormControl<string>('', [Validators.required]),
        timeOffTitle: new FormControl<string>('', [Validators.required]),
        timeOffType: new FormControl<string>('', [
            (control) => {
                const value = control.value;
                if (!value || value === 'Choose One') {
                    return { invalidSelection: true };
                }
                return null;
            },
        ]),
    });

    saving: boolean = false;

    @ViewChild('startTimeInput') startTimeInput: ElementRef;
    @ViewChild('endTimeInput') endTimeInput: ElementRef;

    private startTimeDisplay: TimepickerUI;
    private endTimeDisplay: TimepickerUI;
    private startTimeData: TimePickerType;
    private endTimeData: TimePickerType;
    private themeSubscription: Subscription;

    constructor(
        private dialogRef: MatDialogRef<CreateTimeOffDialogComponent>,
        private timeOffService: TimeOffService,
        private themeService: ThemeService
    ) {}

    ngAfterViewInit() {
        this.themeSubscription = this.themeService.$theme.subscribe(theme => {

            let currentTheme: PickerTheme;

            if (theme == DARKTHEME) {
                currentTheme = PORTALDARKTHEME;
            } else if (theme == LIGHTTHEME) {
                currentTheme = PORTALLIGHTTHEME
            } else {
                currentTheme = PORTALDARKTHEME
            }
            
            this.startTimeDisplay?.destroy();
            this.endTimeDisplay?.destroy();

            this.startTimeDisplay = this.createTimePicker(currentTheme, this.startTimeInput, this.timeOffForm.controls.startTimeDisplay, STARTTIME);
            this.endTimeDisplay = this.createTimePicker(currentTheme, this.endTimeInput, this.timeOffForm.controls.endTimeDisplay, ENDTIME);
        })
    }

    createTimePicker (currentTheme: PickerTheme, timeInput: ElementRef, timeDisplay: FormControl<string>, startOrEnd: StartOrEndTime) {
        const timePicker = new TimepickerUI(timeInput.nativeElement, {
            clock: { incrementMinutes: 15 },
            ui: { cssClass: currentTheme }
        });

        timePicker.create();

        timePicker.on('confirm', (data) => {
            const hour = Number(data.hour);
            let calcHour = hour

            if (data.type == AM && hour == PMTIMESTART) {
                calcHour = AMTIMESTART;
            } else if (data.type == PM && hour < PMTIMESTART) {
                calcHour += PMTIMEADJUSTMENT
            }

            data.hour = calcHour.toString();

            if (startOrEnd == STARTTIME) {
                this.startTimeData = data;
            } else if (startOrEnd == ENDTIME) {
                this.endTimeData = data;
            }

            timeDisplay.setValue(`${hour}:${data.minutes} ${data.type}`);
        });
        return timePicker;
    }
    
    ngOnDestroy() {
        this.themeSubscription?.unsubscribe();
        this.startTimeDisplay?.destroy();
        this.endTimeDisplay?.destroy();
    }

    openStartTime() { // Needed so whole input box is clickable not just the inner input section
        this.startTimeInput.nativeElement.click();
    }

    openEndTime() {
        this.endTimeInput.nativeElement.click();
    }

    disableButtonForInvalidTimes(): boolean {
        const startDate: Date | null = this.timeOffForm.controls.startDate.value;
        const endDate: Date | null = this.timeOffForm.controls.endDate.value;
        const startTime: TimePickerType | null = this.startTimeData;
        const endTime: TimePickerType | null = this.endTimeData;

        if (!startDate || !endDate || !startTime || !endTime) {
            return true;
        }

        const sameDay = startDate.toDateString() === endDate.toDateString();

        if (sameDay) {
            const startDateTime = new Date(startDate);
            const endDateTime = new Date(endDate);

            startDateTime.setHours(
                Number(startTime.hour),
                Number(startTime.minutes),
            );

            endDateTime.setHours(
                Number(endTime.hour),
                Number(endTime.minutes),
            );

            if (endDateTime <= startDateTime) {
                return true;
            }
        }

        return false;
    }

    saveTimeOff(): void {
        this.timeOffForm.markAllAsTouched();

        if (!this.timeOffForm.valid) {
            return;
        }

        const { startDate, endDate } =
            this.timeOffForm.controls;

        if (
            !startDate.value ||
            !endDate.value ||
            !this.startTimeData ||
            !this.startTimeData
        ) {
            return;
        }

        const combineDateAndTime = (date: Date, time: TimePickerType) =>
            new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                Number(time.hour),
                Number(time.minutes),
            );

        const combinedStart = combineDateAndTime(
            startDate.value,
            this.startTimeData
        );
        
        const combinedEnd = combineDateAndTime(
            endDate.value, 
            this.endTimeData
        );

        const newTimeOff: TimeOff = {
            id: null,
            userName: null,
            timeOffTitle: this.timeOffForm.value.timeOffTitle,
            timeOffType: this.timeOffForm.value.timeOffType,
            timeOffDescription: this.timeOffForm.value.timeOffDescription,
            startDate: combinedStart,
            endDate: combinedEnd,
        };
        this.submitTimeOff(newTimeOff);
    }

    private submitTimeOff(timeOff: TimeOff): void {
        this.saving = true;

        this.timeOffService.post([], timeOff).subscribe(() => {
            this.dialogRef.close();
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}
